import React, { useEffect, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Pickaxe, ArrowLeftRight } from 'lucide-react';
type Language = 'es' | 'en' | 'pt';
const translations = {
  es: {
    title: 'Nuestra',
    titleHighlight: 'Economía',
    subtitle: 'Juega y crece con propósito. Cada decisión es importante y tu esfuerzo tiene valor real. Recolecta Vanguard Coins y retíralos vía USDT cuando lo desees.',
    pillars: [{
      title: 'Invertir',
      desc: 'Invierte estratégicamente en items, mejoras y VIP para maximizar tu potencial. Aprovecha las oportunidades del mercado y genera ganancias reales con decisiones inteligentes.'
    }, {
      title: 'Farmear',
      desc: 'Dedica tiempo a farmear recursos y materiales raros que otros jugadores necesitan.'
    }, {
      title: 'Comerciar',
      desc: 'Domina el mercado entre jugadores comprando barato y vendiendo caro.'
    }]
  },
  en: {
    title: 'Our',
    titleHighlight: 'Economy',
    subtitle: 'Play and grow with purpose. Every decision matters and your effort has real value. Collect Vanguard Coins and withdraw them via USDT whenever you wish.',
    pillars: [{
      title: 'Invest',
      desc: 'Invest strategically in items, upgrades, and VIP to maximize your potential. Seize market opportunities and generate real profits with smart decisions.'
    }, {
      title: 'Farm',
      desc: 'Dedicate time to farming rare resources and materials that other players need.'
    }, {
      title: 'Trade',
      desc: 'Master the player market by buying low and selling high.'
    }]
  },
  pt: {
    title: 'Nossa',
    titleHighlight: 'Economia',
    subtitle: 'Jogue e cresça com propósito. Cada decisão é importante e seu esforço tem valor real. Colete Vanguard Coins e retire-os via USDT quando desejar.',
    pillars: [{
      title: 'Investir',
      desc: 'Invista estrategicamente em itens, melhorias e VIP para maximizar seu potencial. Aproveite as oportunidades do mercado e gere lucros reais com decisões inteligentes.'
    }, {
      title: 'Farmar',
      desc: 'Dedique tempo para farmar recursos e materiais raros que outros jogadores precisam.'
    }, {
      title: 'Comercializar',
      desc: 'Domine o mercado entre jogadores comprando barato e vendendo caro.'
    }]
  }
};
const icons = [TrendingUp, Pickaxe, ArrowLeftRight];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
export function Economy() {
  const [language, setLanguage] = useState<Language>('es');
  const t = translations[language];
  useEffect(() => {
    const savedLang = localStorage.getItem('language') as Language;
    if (savedLang) setLanguage(savedLang);
    const handleLanguageChange = (e: CustomEvent<Language>) => {
      setLanguage(e.detail);
    };
    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    return () => window.removeEventListener('languageChange', handleLanguageChange as EventListener);
  }, []);
  return <section className="py-16 md:py-24 relative z-10 overflow-hidden" data-id="element-234">
      {/* Background Image */}
      <div className="absolute inset-0 z-0 pointer-events-none" data-id="element-235">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-80" style={{
        backgroundImage: 'url("https://cdn.magicpatterns.com/uploads/g3njao6M9X5D9k1nAzYJgp/PHOTO-2025-12-20-20-23-39.jpg")'
      }} data-id="element-236" />
        <div className="absolute inset-0 bg-gradient-to-b from-darkerBlue via-darkerBlue/30 to-darkerBlue" data-id="element-237" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-238">
        <div className="text-center mb-16" data-id="element-239">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4" data-id="element-240">
            {t.title}{' '}
            <span className="text-gradient-gold" data-id="element-241">{t.titleHighlight}</span>
          </motion.h2>
          <motion.div initial={{
          opacity: 0,
          scale: 0
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="w-24 h-1 bg-gold mx-auto rounded-full mb-6" data-id="element-242" />
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed" data-id="element-243">
            {t.subtitle}
          </motion.p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-244">
          {t.pillars.map((pillar, index) => {
          const Icon = icons[index];
          return <motion.div key={index} variants={itemVariants} className="glass-panel p-8 rounded-2xl group hover:border-gold/50 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_10px_30px_rgba(212,175,55,0.15)] relative overflow-hidden" data-id="element-245">
                <div className="absolute inset-0 bg-gradient-to-br from-gold/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-246" />

                <div className="relative z-10 flex flex-col items-center text-center" data-id="element-247">
                  <div className="w-16 h-16 rounded-full bg-darkerBlue border border-white/10 flex items-center justify-center mb-6 group-hover:border-gold/50 group-hover:shadow-[0_0_20px_rgba(212,175,55,0.3)] transition-all duration-500" data-id="element-248">
                    <Icon className="w-8 h-8 text-glowBlue group-hover:text-gold transition-colors duration-500" data-id="element-249" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-gold-light transition-colors duration-300 font-cinzel" data-id="element-250">
                    {pillar.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-251">{pillar.desc}</p>
                </div>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}